/**
 * Hook for handling a single file upload with a presigned URL.
 *
 * This hook orchestrates the two-step upload process:
 * 1. Get presigned URL from backend.
 * 2. Upload file directly to S3.
 *
 * @returns Object with uploadFile function.
 */

import { useCallback } from 'react';
import { useGetPresignedUrl } from '@Hooks/Api';
import { uploadToS3 } from '@Helpers/uploadToS3';
import { UploadItem } from '@Types';
import { PresignedURLTypesEnum } from '@Types/Documents';

export interface UploadParams {
  presignedUrlType: PresignedURLTypesEnum;
  filename: string;
  organizationId: number;
  additionalFields?: Record<string, any>;
}

export interface UsePresignedUploadResult {
  uploadFile: (
    file: File,
    params: UploadParams,
    onProgress?: (progress: number) => void,
    abortSignal?: AbortSignal,
  ) => Promise<UploadItem>;
}

export const usePresignedUpload = (): UsePresignedUploadResult => {
  const [getPresignedUrl] = useGetPresignedUrl();

  // Function for fetching presigned URL and uploading file to S3.
  const uploadFile = useCallback(
    async (
      file: File,
      params: UploadParams,
      onProgress?: (progress: number) => void,
      abortSignal?: AbortSignal,
    ): Promise<UploadItem> => {
      // Get presigned URL from backend.
      const response = await getPresignedUrl({
        presigned_url_type: params.presignedUrlType,
        filename: params.filename,
        organization_id: params.organizationId,
        ...(params.additionalFields || {}),
      });

      if (!response?.data) {
        throw new Error('Failed to get presigned URL');
      }

      // Check if upload was cancelled during presigned URL fetch.
      if (abortSignal?.aborted) {
        throw new Error('ABORTED');
      }

      // Upload file directly to S3 using the presigned URL.
      await uploadToS3(response.data.upload_url, file, onProgress, abortSignal);

      // Check if upload was cancelled after S3 upload.
      if (abortSignal?.aborted) {
        throw new Error('ABORTED');
      }

      // Create blob URL for immediate preview of the uploaded file.
      const blobUrl = URL.createObjectURL(file);

      return {
        id: response.data.instance_id,
        filename: params.filename,
        url: blobUrl,
      };
    },
    [getPresignedUrl],
  );

  return { uploadFile };
};
